import { tokenQuoteSchema } from '../types';
import { errorCategories, errorToCodeException } from '../../../../errors';
import { estimateGasLimit } from '../estimateGasLimit';
import { getGasPrice } from 'viem/actions';
import z from 'zod';
const alchemySchema = z.object({
    data: z.array(z.object({
        symbol: z.string(),
        prices: z.array(z.object({
            value: z.string(),
            currency: z.string(),
            lastUpdatedAt: z.string(),
        })),
    })),
});
export async function quoteGasWithUSDC(args) {
    // try to get the token quote from alchemy and if not then fallback to estimate user operation gas
    try {
        const { rpcRequest, accessToken, paymasterProxyAuthSecret, paymasterProxyUrl, enableUsdc, bundlerUrl, smartAccountClient, skipQuoteApi, } = args;
        if (!enableUsdc) {
            return;
        }
        if (!rpcRequest.callData) {
            throw new Error('rpcRequest.callData is required');
        }
        if (skipQuoteApi) {
            return estimateGasLimitAsUsdc({
                smartAccountClient,
                rpcRequest,
                bundlerUrl,
            });
        }
        const sponsorUserOpUrl = `${paymasterProxyUrl}/api/quote-gas-with-usdc`;
        const response = await fetch(sponsorUserOpUrl, {
            method: 'POST',
            body: JSON.stringify({
                data: rpcRequest,
            }),
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${paymasterProxyAuthSecret}`,
                'X-PM-Token': accessToken ?? '',
            },
        });
        const json = await response.json();
        if (!response.ok) {
            const error = new Error(`[getTokenQuote] Error getting token quote:: ${response.status} ${response.statusText} ${JSON.stringify(json)}`);
            const errorCategory = errorCategories.userop_pm_error_code;
            throw errorToCodeException(error, errorCategory);
        }
        const parseResult = tokenQuoteSchema.safeParse(json);
        if (!parseResult.success) {
            throw new Error(`[getTokenQuote] Error parsing token quote:: ${JSON.stringify(parseResult.error)}`);
        }
        const parsedData = parseResult.data.data;
        if (parsedData.estimate === '0') {
            throw new Error('No USDC estimate found');
        }
        return parsedData;
    }
    catch (error) {
        const { bundlerUrl, smartAccountClient, rpcRequest } = args;
        return estimateGasLimitAsUsdc({
            smartAccountClient,
            rpcRequest,
            bundlerUrl,
        });
    }
}
async function estimateGasLimitAsUsdc(args) {
    const { smartAccountClient, rpcRequest, bundlerUrl } = args;
    // hacky and we should pass this as a param
    const apiKey = bundlerUrl.split('/').pop();
    if (!smartAccountClient.account || !smartAccountClient.client) {
        throw new Error('Smart account client is required');
    }
    const gasEstimate = await estimateGasLimit({
        userOp: rpcRequest,
        account: smartAccountClient.account,
        client: smartAccountClient.client,
    });
    const totalGas = (gasEstimate?.callGasLimit ?? 0n) +
        (gasEstimate?.preVerificationGas ?? 0n) +
        (gasEstimate?.verificationGasLimit ?? 0n);
    const gasPrice = await getGasPrice(smartAccountClient.client); // in wei
    const response = await fetch(`https://api.g.alchemy.com/prices/v1/${apiKey}/tokens/by-symbol?symbols=ETH`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
    });
    const data = await response.json();
    const parsed = alchemySchema.safeParse(data);
    if (!parsed.success) {
        throw new Error(`Failed to fetch ETH price from Alchemy, zod parse failed: ${parsed.error.message}`);
    }
    const ethPrice = parsed.data.data[0].prices[0].value;
    const ethPriceInUSD = parseFloat(ethPrice); // "3000.000" -> 3000.000
    const ethPriceInUSDCPerETH = BigInt(Math.floor(ethPriceInUSD * 1e6)); // 3000 * 10^6
    const costInWei = totalGas * gasPrice;
    const gasCostInUSDC = (costInWei * ethPriceInUSDCPerETH) / 10n ** 18n;
    const bufferPercentage = 50;
    const gasCostInUSDCWithBuffer = gasCostInUSDC + (gasCostInUSDC * BigInt(bufferPercentage)) / 100n;
    return {
        decimals: 6,
        estimate: gasCostInUSDCWithBuffer.toString(),
    };
}
